import { UnitOfDistance } from '@treadinc/horizon-api-spec';
import { isNil } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';

import { WaypointSchema } from '~constants/regexConst';
import { DeparsedRouteEstimate } from '~hooks/useRoute/models';
import useRoute from '~hooks/useRoute/useRoute';
import { useStores } from '~store';
import {
  convertMeters,
  getStringOrNumberFieldPositiveNumberValue,
  numericFormValuesMatch,
} from '~utils/utilFunctions';

type RouteEstimateArgs = DeparsedRouteEstimate;

type CycleEstimateArgs = {
  dropoffOnsite: number;
  pickupOnsite: number;
  pickupToDropoff: number;
};

interface RouteEstimateEffectsProps {
  initialCycleDistance?: number;
  initialCycleTime?: number;
  initialPickupToDropoff?: number;
}

export default function RouteEstimateEffects({
  initialCycleDistance,
  initialCycleTime,
  initialPickupToDropoff,
}: RouteEstimateEffectsProps) {
  const form = useFormContext();
  // The order form submits only dirty fields and the backend never recomputes the cycle,
  // so a re-estimate that stays clean leaves dispatch on the stale one. An estimate that
  // lands while the form is pristine is seeding the display instead. Read through a ref
  // because adding the flag to the effect deps below fires a second route estimate for
  // the same route.
  const { isDirty } = useFormState({ control: form.control });
  const formIsDirtyRef = useRef(isDirty);
  formIsDirtyRef.current = isDirty;

  const { userStore } = useStores();
  const { getRouteEstimate } = useRoute();

  const shouldUseKilometers = userStore.userCompany.isMeters;

  const watchedDropOffWayPoint = form.watch('dropOffWayPoint') as WaypointSchema | null;
  const watchedPickUpWayPoint = form.watch('pickUpWayPoint') as WaypointSchema | null;
  const watchedDropoffOnsite = form.watch('dropoffOnsite') as string | number | null;
  const watchedPickupOnsite = form.watch('pickupOnsite') as string | number | null;
  const watchedPickupToDropoff = form.watch('pickupToDropoff') as string | number | null;

  const isEditing = Boolean(form.watch('id'));
  const routeEstimateOverridePrevented = useRef(!isEditing);
  const cycleEstimateOverridePrevented = useRef(!isEditing);

  const routeEstimateArgs: RouteEstimateArgs | null = useMemo(() => {
    const pickupLat = watchedPickUpWayPoint?.site?.lat;
    const pickupLon = watchedPickUpWayPoint?.site?.lon;
    const dropoffLat = watchedDropOffWayPoint?.site?.lat;
    const dropoffLon = watchedDropOffWayPoint?.site?.lon;

    const canEstimate = [pickupLat, pickupLon, dropoffLat, dropoffLon].every((value) => {
      return !isNil(value);
    });

    if (!canEstimate) {
      return null;
    }

    // check if we should prevent firing a route estimate, as it will override the initial cycle
    // distance & pickup to dropoff values. we only need to skip the first route estimate.
    let shouldPreventRouteEstimate = false;

    if (isEditing) {
      const hasInitialCycleDistance = !isNil(initialCycleDistance);
      const hasInitialPickupToDropoff = !isNil(initialPickupToDropoff);
      const routeEstimateOverrideHasAlreadyBeenPrevented =
        routeEstimateOverridePrevented.current;

      if (
        !routeEstimateOverrideHasAlreadyBeenPrevented &&
        (hasInitialCycleDistance || hasInitialPickupToDropoff)
      ) {
        routeEstimateOverridePrevented.current = true;
        shouldPreventRouteEstimate = true;
      }
    }

    if (shouldPreventRouteEstimate) {
      return null;
    }

    return {
      pickupSiteLat: Number(pickupLat),
      pickupSiteLon: Number(pickupLon),
      dropoffSiteLat: Number(dropoffLat),
      dropoffSiteLon: Number(dropoffLon),
    };
  }, [
    initialCycleDistance,
    initialPickupToDropoff,
    isEditing,
    watchedDropOffWayPoint,
    watchedPickUpWayPoint,
  ]);

  const cycleEstimateArgs: CycleEstimateArgs | null = useMemo(() => {
    const pickupOnsite = getStringOrNumberFieldPositiveNumberValue(watchedPickupOnsite);
    const dropoffOnsite = getStringOrNumberFieldPositiveNumberValue(watchedDropoffOnsite);
    const pickupToDropoff =
      getStringOrNumberFieldPositiveNumberValue(watchedPickupToDropoff);

    const canEstimate = [pickupOnsite, dropoffOnsite, pickupToDropoff].every((value) => {
      return !isNil(value);
    });

    if (!canEstimate) {
      return null;
    }

    return {
      pickupOnsite: Number(pickupOnsite),
      dropoffOnsite: Number(dropoffOnsite),
      pickupToDropoff: Number(pickupToDropoff),
    };
  }, [watchedPickupOnsite, watchedDropoffOnsite, watchedPickupToDropoff]);

  const estimateRoute = useCallback(async () => {
    if (!routeEstimateArgs) {
      return;
    }

    const estimate = await getRouteEstimate(routeEstimateArgs);

    if (!estimate) {
      return;
    }

    const cycleDistance = convertMeters(
      estimate.pickupToDropOffDistanceMeters,
      shouldUseKilometers ? UnitOfDistance.KILOMETER : UnitOfDistance.MILE,
    );

    return { cycleDistance, pickupToDropoff: estimate.pickupToDropOffTimeMinutes };
  }, [JSON.stringify(routeEstimateArgs), shouldUseKilometers]);

  const estimateCycle = useCallback(() => {
    if (!cycleEstimateArgs) {
      return;
    }

    const { pickupOnsite, dropoffOnsite, pickupToDropoff } = cycleEstimateArgs;

    return pickupOnsite + dropoffOnsite + pickupToDropoff * 2;
  }, [JSON.stringify(cycleEstimateArgs)]);

  useEffect(() => {
    estimateRoute().then((estimate) => {
      if (!isNil(estimate)) {
        const cycleDistance = estimate.cycleDistance.toFixed(2);
        const pickupToDropoff = `${estimate.pickupToDropoff}`;

        if (!numericFormValuesMatch(form.getValues('cycleDistance'), cycleDistance)) {
          form.setValue('cycleDistance', cycleDistance, {
            shouldDirty: formIsDirtyRef.current,
          });
        }

        if (!numericFormValuesMatch(form.getValues('pickupToDropoff'), pickupToDropoff)) {
          form.setValue('pickupToDropoff', pickupToDropoff, {
            shouldDirty: formIsDirtyRef.current,
          });
        }
      }
    });
  }, [estimateRoute]);

  useEffect(() => {
    const cycleTime = estimateCycle();

    if (!isNil(cycleTime)) {
      // prevent cycle time override it is being calculated for the fist time
      if (
        !cycleEstimateOverridePrevented.current &&
        initialCycleTime &&
        cycleTime !== initialCycleTime
      ) {
        cycleEstimateOverridePrevented.current = true;
        return;
      }

      if (!numericFormValuesMatch(form.getValues('cycleTime'), cycleTime)) {
        form.setValue('cycleTime', `${cycleTime}`, {
          shouldDirty: formIsDirtyRef.current,
        });
      }
    }
  }, [estimateCycle, initialCycleTime]);

  return null;
}
